import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export type KioskProductAddPagePreviewImage = {
  url: string;
  width?: number;
  height?: number;
};

export type KioskProductAddPagePreviewProduct = {
  id: string;
  _id: string;
  shop_id: string;
  name: string;
  description?: string;
  price: number;
  final_price: number;
  vat: string;
  type: 'SIMPLE' | 'DECLINABLE';
  available: boolean;
  additions_group: string;
  categories: readonly KioskProductAddPagePreviewCategory[];
  additions: {
    products: readonly string[];
  };
  images?: {
    thumbnail?: KioskProductAddPagePreviewImage;
    cover?: KioskProductAddPagePreviewImage;
  };
};

export type KioskProductAddPagePreviewCategory = {
  count: number;
  min_count: number;
  products: readonly string[];
  store_id: string;
  store: string;
};

export type KioskProductAddPagePreviewAdditionGroup = {
  id: string;
  name: string;
  min_count: number;
  max_count?: number;
  items: readonly KioskProductAddPagePreviewProduct[];
  count?: number;
  position?: number;
};

export type KioskProductAddPagePreviewShop = {
  id: string;
  name: string;
  slug: string;
  available: boolean;
  distribution_modes: readonly {
    type: string;
    enabled: boolean;
    label: string;
    priority: number;
    preordering_allowed: boolean;
    preordering_min_delay: number;
    preordering_max_delay: number;
  }[];
  addition_groups: readonly KioskProductAddPagePreviewAdditionGroup[];
  products: readonly KioskProductAddPagePreviewProduct[];
  store: readonly {
    id: string;
    name: string;
    available: boolean;
    display_type: string;
    products: readonly string[];
  }[];
};

/**
 * Serializable preview content for `KioskProductAddPageComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type KioskProductAddPageScenarioContent = {
  shopId: string;
  shop: KioskProductAddPagePreviewShop;
  product: KioskProductAddPagePreviewProduct;
};
export const KIOSKPRODUCTADDPAGE_LEGACY_FIELD_ORDER = [
  'background_color',
  'background_image',
  'text_color',
  { type: 'separator' },
  'card_color',
  { type: 'separator' },
  'button_background_color',
  'button_text_color',
  { type: 'separator' },
  'secondary_color',
  { type: 'separator' },
  'unavailable_button_background',
  'unavailable_button_text',
  { type: 'separator' },
  'composition_background_color',
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskProductAddPageDescriptor: _generatedKioskProductAddPageDescriptor,
  kioskProductAddPageBlocDefinition,
  kioskProductAddPageDashboardLayout,
  kioskProductAddPageConfig,
  kioskProductAddPageResolveConfig,
  kioskProductAddPageProperties,
  kioskProductAddPageDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: 'Kiosk - Product add page',
    internal_name: 'Kiosk - Product add page',
    url: 'products/:productId/add',
    defaultParent: [/^Kiosk - Shop page$/i, /^Kiosk - Category page$/i],
    componentType: 'KioskProductAddPageComponent',
    category: 'page',
    description: 'Kiosk - Product add page.',
    keywords: [],
    isRootBlock: true,
    sharedFields: {},
  },
  () => ({
    background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#FFFFFF' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.background-color.label',
      },
    }),
    background_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-product-add-page.background-image.label',
        help: 'pages.blocks.kiosk-product-add-page.background-image.help',
      },
    }),
    text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#000' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.text-color.label',
      },
    }),
    card_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.card-color.label',
      },
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.button-background-color.label',
      },
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.button-text-color.label',
      },
    }),
    secondary_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.secondary-color.label',
        help: 'pages.blocks.kiosk-product-add-page.secondary-color.help',
      },
    }),
    unavailable_button_background: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.unavailable-button-background.label',
      },
    }),
    unavailable_button_text: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.unavailable-button-text.label',
      },
    }),
    composition_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.kiosk-product-add-page.composition-background-color.label',
      },
    }),
  }),
  () => ({
    tabs: [
      {
        label: 'pages.blocks.kiosk-product-add-page.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-product-add-page.fieldset.page.label',
            fields: ['background_color', 'background_image', 'text_color'],
          },
          {
            legend: 'pages.blocks.kiosk-product-add-page.fieldset.product-card.label',
            fields: ['card_color', 'secondary_color'],
          },
          {
            legend: 'pages.blocks.kiosk-product-add-page.fieldset.composition.label',
            fields: ['composition_background_color'],
          },
          {
            legend: 'pages.blocks.kiosk-product-add-page.fieldset.add-button.label',
            fields: ['button_background_color', 'button_text_color'],
          },
          {
            legend: 'pages.blocks.kiosk-product-add-page.fieldset.unavailable-button.label',
            fields: ['unavailable_button_background', 'unavailable_button_text'],
          },
        ],
      },
    ],
  })
);

export function kioskProductAddPageDescriptor(): IBlockDescriptor;
export function kioskProductAddPageDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskProductAddPageDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskProductAddPageDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedKioskProductAddPageDescriptor, kioskProductAddPageProperties, fieldOrder);
}

export {
  kioskProductAddPageBlocDefinition,
  kioskProductAddPageDashboardLayout,
  kioskProductAddPageProperties,
  kioskProductAddPageConfig,
  kioskProductAddPageResolveConfig,
  kioskProductAddPageDefinitionSetupScenarios,
};

export type KioskProductAddPageConfig = typeof kioskProductAddPageConfig;
